import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <div className="glass-panel rounded-2xl p-8 border text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#22c55e' }} />
            <h1 className="text-2xl font-bold mb-4" style={{ color: '#ffffff' }}>Check Your Email</h1>
            <p className="text-muted-foreground mb-6">
              If an account exists for <strong style={{ color: '#D4AF37' }}>{email}</strong>, you will receive password reset instructions shortly.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Didn't receive an email? Check your spam folder or make sure you entered the correct email address.
            </p>
            <Link to="/login">
              <Button 
                variant="outline" 
                className="rounded-full"
                data-testid="back-to-login-button"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="glass-panel rounded-2xl p-8 border">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
              <Mail size={32} style={{ color: '#D4AF37' }} />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#ffffff' }} data-testid="forgot-password-title">
              Forgot Your Password?
            </h1>
            <p className="text-muted-foreground">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1"
                data-testid="forgot-password-email-input"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full rounded-full transition-all duration-300"
              disabled={loading}
              style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
              data-testid="forgot-password-submit-button"
            >
              {loading ? 'Sending...' : 'Send Reset Instructions'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm hover:underline flex items-center justify-center gap-2" style={{ color: '#a3a3a3' }}>
              <ArrowLeft size={14} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
